<template>
  <div class="form-builder">
    <TransitionGroup name="list" tag="ol" class="list-unstyled">
      <template v-for="(item, index) in items" :key="`transition_children_${item.key}_${item.position}`">

        <SortableItem
          :item="item"
          :index="index"
          :scope="scope"
          :total-items="items.length"
          :types="types"
          @toggle-delete="toggleDeleteItem(item)"
          @toggle-new-options="toggleNewItemsOptions(index)"
          @move-up="moveUp(index)"
          @move-down="moveDown(index)"
          @mouseover="showItemIcons(item)"
          @mouseleave="hideBlockIcons(item)"
          @toggle-accordion="toggleAccordion($event)"
        >
          <fieldset :disabled='item._destroy'>
            <slot :item='item' :index='index' />
          </fieldset>

          <input
            v-if="item._destroy"
            type="hidden"
            :name="scopeToName([...scope, items.length+index, 'id'])"
            :value="item.id"
          >
          <input
            v-if="item._destroy"
            type="hidden"
            :name="scopeToName([...scope, items.length+index, '_destroy'])"
            value="true"
          >
        </SortableItem>

        <!-- New block options -->
        <li
            key="new_item_popup"
            v-if="newItemsPopupPosition === index"
            class="d-flex justify-content-center w-100 mb-3"
        >
          <NewItemOptions
            :types="types"
            :disable-image="formHasImage"
            @input="type => { addNewItem(type, index+1); toggleNewItemsOptions(-1) }"
          />
        </li>
      </template>

      <!--  Block options (toggle)  -->
      <li class="d-flex justify-content-center w-100" key="transition_children_new_item">
        <NewItemOptions
          v-if="newItemsPopupPosition >= items.length"
          :types="types"
          :disable-image="formHasImage"
          @input="type => { addNewItem(type); toggleNewItemsOptions(-1) }"
        />
        <button
          v-else
          type="button"
          class="btn btn-dark icon-plus plus-btn"
          v-on:click="toggleNewItemsOptions(items.length)">
        </button>
      </li>
    </TransitionGroup>
  </div>
</template>

<script lang="ts">
import type { EveryBuildableItem, ItemType, PropType } from "@/types";
import { defineComponent } from "vue";
import NewItemOptions from "./NewItemOptions.vue";
import { scopeToName } from "@/lib/candidacy/form_builder/utils";
import { randomInteger } from "@/lib/RandomKey";
import SortableItem from "./SortableItem.vue";
import { Collapse } from "@assemblyvoting/electa-ui";

export default defineComponent({
  components: {
    SortableItem,
    NewItemOptions,
  },
  name: "SortableBuilder",
  props: {
    content: {
      type: Array as PropType<EveryBuildableItem[]>,
      default() {
        return []
      }
    },
    types: {
      type: Array as PropType<ItemType[]>,
      required: true,
    },
    scope: {
      type: Array as PropType<string[]>,
      required: true,
    }
  },
  created() {
    this.content.forEach((item: EveryBuildableItem, index: number) => {
      this.items.push({
        ...item,
        key: index,
        showIcons: false,
        showItemOptions: false,
        opened: false,
      })
    });
    this.sortByPosition();
  },
  data() {
    return {
      newItemsPopupPosition: -1,
      items: [] satisfies EveryBuildableItem[],
    }
  },
  computed: {
    formHasImage() {
      return this.items.some((item: EveryBuildableItem) => item.item_type === "image") && this.scope[0] === 'form';
    }
  },
  methods: {
    scopeToName,
    toggleNewItemsOptions(position: number | null) {
      if (this.newItemsPopupPosition === position) {
        this.newItemsPopupPosition = -1;
      } else {
        this.newItemsPopupPosition = position;
      }
    },
    addNewItem(itemType: string, position: number | null = null) {
      const item = {
        key: this.generateUniqueKey(),
        type: this.types.find((t: ItemType) => t.item_type === itemType).type,
        content: "",
        data: {},
        item_type: itemType,
        showIcons: false,
        showItemOptions: false,
        opened: false,
      }

      if (position === null) {
        this.items.push(item);
      } else {
        this.items.splice(position, 0, item);
      }

      this.openNewItem(position);
    },
    openNewItem(position: number) {
      const index: number = position || this.items.length - 1;
      if (this.items[index].item_type === "divider") return;
      this.closeAccordions();
      this.items[index].opened = true;
      setTimeout(() => { // Wait some time for the element to appear
        const createdItem = document.querySelectorAll('.accordion-collapse:not([data-exclude=true])')[index];
        Collapse.getOrCreateInstance(createdItem).show();
      }, 100);
    },
    moveUp(index: number) {
      if (index <= 0) return;

      const firstIndex = index-1;
      const lastIndex = index;
      this.items.splice(firstIndex, 2, this.items[lastIndex], this.items[firstIndex])
    },
    moveDown(index: number) {
      if (index+1 >= this.items.length) return;

      const firstIndex = index;
      const lastIndex = index+1;
      this.items.splice(firstIndex, 2, this.items[lastIndex], this.items[firstIndex])
    },
    showItemIcons(item: EveryBuildableItem) {
      item.showIcons = true
    },
    hideBlockIcons(item: EveryBuildableItem) {
      item.showIcons = false
    },
    sortByPosition() {
      this.items.sort((item1: EveryBuildableItem, item2: EveryBuildableItem) => (item1.position > item2.position) ? 1 : (item2.position > item1.position) ? -1 : 0)
    },
    toggleDeleteItem(item: EveryBuildableItem) {
      this.items.find((i: EveryBuildableItem) => i.key === item.key)._destroy = !item._destroy;
    },
    generateUniqueKey() {
      let key: number;
      const usedKeys = this.items.map((i: EveryBuildableItem) => i.key);
      do {
        key = randomInteger();
      } while (usedKeys.includes(key));
      return key;
    },
    closeAccordions() {
      this.items.forEach((item: EveryBuildableItem) => item.opened = false);
    },
    toggleAccordion(item: EveryBuildableItem) {
      const previousStatus = item.opened;
      this.closeAccordions();
      this.items.find((i: EveryBuildableItem) => item.key === i.key).opened = !previousStatus;
    },
  },
});
</script>

<style scoped>
.form-builder {
  margin-bottom: 1rem;
}
.plus-btn {
  width: 8rem;
}

.list-move, /* apply transition to moving elements */
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.list-leave-active {
  position: absolute;
}

.accordion-button::after {
  content: none;
}
</style>
